import api from "./api";
import { ApiResponse, PaginatedResponse } from "../types/common";

export type YesNo = "" | "yes" | "no";

export interface DailyChecklistPayload {
  walkInInquiries: string;
  registrationsCompleted: string;
  deferredFollowUp: string;
  referralWhatsapp: string;
  referralTiktok: string;
  referralFacebook: string;
  referralFamilyFriend: string;
  referralBillboard: string;
  referralFlyers: string;
  referralOther: string;
  whatsappInquiriesReceived: string;
  facebookInquiriesComments: string;
  tiktokCommentsHandled: string;
  jobApplicationEmails: string;
  paymentsRecordedKes: string;
  cashReceivedKes: string;
  receiptsIssued: string;
  pettyCashSpentKes: string;
  remainingImprestBalanceKes: string;
  suppliesChecked: YesNo;
  suppliesNotes: string;
  issuesEscalated: YesNo;
  issuesNotes: string;
  frontOfficeOrderly: YesNo;
  frontOfficeNotes: string;
  secretaryName: string;
  date: string;
  signature: string;
}

export interface DailyChecklistRecord {
  _id: string;
  branchId: string;
  secretaryUserId: string;
  date: string;
  walkInInquiries: number;
  registrationsCompleted: number;
  deferredFollowUp: number;
  referralWhatsapp: number;
  referralTiktok: number;
  referralFacebook: number;
  referralFamilyFriend: number;
  referralBillboard: number;
  referralFlyers: number;
  referralOther: string;
  whatsappInquiriesReceived: number;
  facebookInquiriesComments: number;
  tiktokCommentsHandled: number;
  jobApplicationEmails: number;
  paymentsRecordedKes: number;
  cashReceivedKes: number;
  receiptsIssued: number;
  pettyCashSpentKes: number;
  remainingImprestBalanceKes: number;
  suppliesChecked: YesNo;
  suppliesNotes: string;
  issuesEscalated: YesNo;
  issuesNotes: string;
  frontOfficeOrderly: YesNo;
  frontOfficeNotes: string;
  secretaryName: string;
  signature: string;
  createdAt: string;
  updatedAt: string;
}

export interface DailyChecklistHistoryFilters {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export type SupplyStatus = "" | "ok" | "low" | "critical";

export interface WeeklyReportPayload {
  totalWalkInInquiries: string;
  totalRegistrations: string;
  referralWhatsapp: string;
  referralTiktok: string;
  referralFacebook: string;
  referralFamilyFriend: string;
  referralBillboard: string;
  referralFlyers: string;
  referralOther: string;
  whatsappInquiries: string;
  facebookInquiries: string;
  tiktokInquiries: string;
  jobRelatedEmails: string;
  totalPaymentsRecordedKes: string;
  totalCashReceivedKes: string;
  totalPettyCashUsedKes: string;
  remainingImprestBalanceKes: string;
  suppliesStatus: SupplyStatus;
  itemsNeededNextWeek: string;
  challengesFaced: string;
  systemDowntimeIncidents: string;
  recommendations: string;
  secretaryName: string;
  weekEnding: string;
  signature: string;
}

export interface WeeklyReportRecord {
  _id: string;
  branchId: string;
  secretaryUserId: string;
  weekEnding: string;
  totalWalkInInquiries: number;
  totalRegistrations: number;
  referralWhatsapp: number;
  referralTiktok: number;
  referralFacebook: number;
  referralFamilyFriend: number;
  referralBillboard: number;
  referralFlyers: number;
  referralOther: string;
  whatsappInquiries: number;
  facebookInquiries: number;
  tiktokInquiries: number;
  jobRelatedEmails: number;
  totalPaymentsRecordedKes: number;
  totalCashReceivedKes: number;
  totalPettyCashUsedKes: number;
  remainingImprestBalanceKes: number;
  suppliesStatus: SupplyStatus;
  itemsNeededNextWeek: string;
  challengesFaced: string;
  systemDowntimeIncidents: string;
  recommendations: string;
  secretaryName: string;
  signature: string;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyReportHistoryFilters {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
}

export const saveDailyChecklist = async (
  payload: DailyChecklistPayload,
): Promise<ApiResponse<DailyChecklistRecord>> => {
  return api.post("/secretary-reports/daily-checklists", payload);
};

export const getDailyChecklistHistory = async (
  filters: DailyChecklistHistoryFilters = {},
): Promise<PaginatedResponse<DailyChecklistRecord>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", String(filters.page));
  if (filters.limit) params.append("limit", String(filters.limit));
  if (filters.search) params.append("search", filters.search);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const query = params.toString();
  const response = await api.get(
    query
      ? `/secretary-reports/daily-checklists?${query}`
      : "/secretary-reports/daily-checklists",
  );

  const data = response as any;
  return {
    success: !!data?.success,
    data: data?.data || [],
    pagination: {
      currentPage: data?.pagination?.currentPage || data?.currentPage || 1,
      totalPages: data?.pagination?.totalPages || data?.totalPages || 1,
      totalItems: data?.pagination?.totalItems || data?.total || 0,
      hasNext:
        data?.pagination?.hasNext ||
        (data?.currentPage || 1) < (data?.totalPages || 1),
      hasPrev: data?.pagination?.hasPrev || (data?.currentPage || 1) > 1,
    },
  };
};

export const saveWeeklyReport = async (
  payload: WeeklyReportPayload,
): Promise<ApiResponse<WeeklyReportRecord>> => {
  return api.post("/secretary-reports/weekly-reports", payload);
};

export const getWeeklyReportHistory = async (
  filters: WeeklyReportHistoryFilters = {},
): Promise<PaginatedResponse<WeeklyReportRecord>> => {
  const params = new URLSearchParams();

  if (filters.page) params.append("page", String(filters.page));
  if (filters.limit) params.append("limit", String(filters.limit));
  if (filters.search) params.append("search", filters.search);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const query = params.toString();
  const response = await api.get(
    query
      ? `/secretary-reports/weekly-reports?${query}`
      : "/secretary-reports/weekly-reports",
  );

  const data = response as any;
  return {
    success: !!data?.success,
    data: data?.data || [],
    pagination: {
      currentPage: data?.pagination?.currentPage || data?.currentPage || 1,
      totalPages: data?.pagination?.totalPages || data?.totalPages || 1,
      totalItems: data?.pagination?.totalItems || data?.total || 0,
      hasNext:
        data?.pagination?.hasNext ||
        (data?.currentPage || 1) < (data?.totalPages || 1),
      hasPrev: data?.pagination?.hasPrev || (data?.currentPage || 1) > 1,
    },
  };
};
